import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ScrollArea } from "./ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  MessageSquare,
  Search,
  Loader2,
  Users,
  History,
  Bookmark,
  Store,
  UtensilsCrossed,
  Check,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

export const NewChatDialog = ({ 
  open, 
  onOpenChange, 
  onSelectConversation, // NUEVO: callback para seleccionar conversación sin navegar
  onOpenProfile,
  currentUserId,
  existingConversations = []
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startingChat, setStartingChat] = useState(null);
  const [savingUser, setSavingUser] = useState(null);
  const [showSaveMenu, setShowSaveMenu] = useState(null);
  const [savedSuccess, setSavedSuccess] = useState({});

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setSearchResults([]);
      setStartingChat(null);
      setShowSaveMenu(null);
      setSavedSuccess({});
      
      const usersFromConversations = existingConversations
        .filter(conv => !conv.is_group)
        .map(conv => {
          const otherUser = conv.participant_info?.find(p => p.user_id !== currentUserId) || conv.participant_info?.[0];
          return otherUser;
        })
        .filter(Boolean);
      
      const uniqueUsers = usersFromConversations.reduce((acc, user) => {
        if (!acc.find(u => u.user_id === user.user_id)) {
          acc.push(user);
        }
        return acc;
      }, []);
      
      setRecentUsers(uniqueUsers);
    }
  }, [open, existingConversations, currentUserId]);

  const searchUsers = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/search?q=${encodeURIComponent(query.trim())}&search_type=users`, {
        headers: { ...getAuthHeaders() },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        const userList = data.users || [];
        setSearchResults(userList.filter(u => u.user_id !== currentUserId));
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error("Error searching users:", err);
      setSearchResults([]);
    }
    setLoading(false);
  }, [currentUserId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        searchUsers(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchUsers]);

  // SOLUCIÓN SIN NAVEGACIÓN: Crear conversación y seleccionarla directamente
  const handleStartChat = async (userId) => {
    if (startingChat) return;
    setStartingChat(userId);
    
    const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
    
    try {
      const response = await fetch(`${API}/api/conversations`, {
        method: "POST",
        headers: headers,
        credentials: "include",
        body: JSON.stringify({ participant_ids: [userId], is_group: false }),
      });

      if (response.ok) {
        const conv = await response.json();
        
        // Cerrar el diálogo
        onOpenChange(false);
        
        // Llamar al callback para seleccionar la conversación (SIN NAVEGACIÓN)
        if (onSelectConversation) {
          onSelectConversation(conv);
        }
        
        toast.success("Chat iniciado");
      } else {
        let errorMsg = "Error al iniciar chat";
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch (e) {}
        toast.error(errorMsg);
        setStartingChat(null);
      }
    } catch (err) {
      console.error("Network error:", err);
      toast.error("Error de conexión");
      setStartingChat(null);
    }
  };

  const handleSaveToCategory = async (user, category) => {
    setSavingUser(user.user_id);
    try {
      const res = await fetch(`${API}/api/user/saved/${category}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ category, user_id: user.user_id })
      });
      if (res.ok) {
        const categoryNames = {
          contacts: "Contactos",
          stores: "Mis Tiendas",
          restaurants: "Mis Restaurantes"
        };
        toast.success(`Guardado en ${categoryNames[category]}`);
        setSavedSuccess({ ...savedSuccess, [user.user_id]: category });
        setShowSaveMenu(null);
      } else {
        toast.error("Error al guardar");
      }
    } catch (err) {
      toast.error("Error al guardar");
    }
    setSavingUser(null);
  };

  const displayUsers = searchQuery.trim().length >= 2 ? searchResults : recentUsers;
  const showingSearchResults = searchQuery.trim().length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#17212b] border-[#0e1621] w-[95vw] max-w-lg sm:max-w-xl p-0 overflow-hidden max-h-[90vh]">
        <div className="p-5 pb-0">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <img src="/logo-okgo-load.png" alt="okgo" className="h-12" />
            </div>
            <DialogTitle className="text-white text-center text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Nuevo chat
              <span className="block text-sm font-normal text-slate-400 mt-1">Busca un usuario para chatear</span>
            </DialogTitle>
          </DialogHeader>
        </div>
        
        <div className="p-5 pt-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input 
              type="text"
              placeholder="Buscar usuario por nombre o nickname..." 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              className="pl-10 bg-[#242f3d] border-transparent text-white placeholder:text-slate-500 h-11 text-base" 
            />
          </div>

          <div className="flex items-center gap-2 text-slate-400 text-sm">
            {showingSearchResults ? (
              <>
                <Search className="w-4 h-4" />
                <span>Resultados de búsqueda ({searchResults.length})</span>
              </>
            ) : (
              <>
                <History className="w-4 h-4" />
                <span>Chats recientes ({recentUsers.length})</span>
              </>
            )}
          </div>

          <ScrollArea className="h-[45vh] sm:h-[350px] max-h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-red-500" />
              </div>
            ) : displayUsers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-14 h-14 mx-auto mb-4 opacity-50" />
                {showingSearchResults ? (
                  <>
                    <p className="text-base">No se encontraron usuarios</p>
                    <p className="text-sm mt-1">Intenta con otro nombre o nickname</p>
                  </>
                ) : (
                  <>
                    <p className="text-base">No tienes chats recientes</p>
                    <p className="text-sm mt-1">Busca un usuario para iniciar una conversación</p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-2 pr-2">
                {displayUsers.map((user) => (
                  <div 
                    key={user.user_id} 
                    className="p-3 rounded-lg bg-[#1e2a36] hover:bg-[#232e3c] transition-colors"
                  >
                    <div className="flex items-center">
                      <div 
                        className="cursor-pointer"
                        onClick={() => onOpenProfile && onOpenProfile(user)}
                      >
                        <Avatar className="w-11 h-11 flex-shrink-0 hover:ring-2 hover:ring-red-500/50 transition-all">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback className="bg-gradient-to-br from-red-500 to-purple-600 text-white text-base">
                            {user.name?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      
                      <div 
                        className="ml-3 flex-1 min-w-0 cursor-pointer"
                        onClick={() => onOpenProfile && onOpenProfile(user)}
                      >
                        <p className="font-medium text-white text-base truncate">
                          {user.name}
                        </p>
                        <p className="text-sm text-slate-500 truncate">@{user.nickname || user.email?.split('@')[0]}</p>
                      </div>
                      
                      {user.status === 'online' && (
                        <div className="w-2.5 h-2.5 bg-green-500 rounded-full mr-2 flex-shrink-0" />
                      )}
                    </div>

                    <div className="flex gap-2 mt-3">
                      {/* BOTÓN CHATEAR - Sin navegación, solo selección */}
                      <button
                        type="button"
                        disabled={startingChat === user.user_id}
                        onClick={() => handleStartChat(user.user_id)}
                        className={`flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium rounded-lg transition-colors select-none ${
                          startingChat === user.user_id 
                            ? 'bg-red-800 text-white' 
                            : 'bg-red-600 hover:bg-red-700 active:bg-red-800 text-white'
                        }`}
                        data-testid={`chat-btn-${user.user_id}`}
                      >
                        {startingChat === user.user_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Chatear
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowSaveMenu(showSaveMenu === user.user_id ? null : user.user_id)}
                        disabled={savingUser === user.user_id}
                        className={`px-4 py-3 rounded-lg transition-colors flex items-center justify-center ${
                          savedSuccess[user.user_id] 
                            ? 'bg-green-600 text-white'
                            : 'bg-[#2b5278] hover:bg-[#3a6a99] text-white'
                        }`}
                      >
                        {savingUser === user.user_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : savedSuccess[user.user_id] ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Bookmark className="w-4 h-4" />
                        )}
                      </button>
                    </div>

                    {showSaveMenu === user.user_id && (
                      <div className="mt-2 p-2 bg-[#17212b] rounded-lg border border-[#2b5278]">
                        <p className="text-xs text-slate-400 mb-2 px-2">Guardar en:</p>
                        <div className="space-y-1">
                          <button
                            type="button"
                            onClick={() => handleSaveToCategory(user, "contacts")}
                            className="w-full flex items-center px-3 py-2 text-sm text-white hover:bg-[#2b5278] rounded-md"
                          >
                            <Users className="w-4 h-4 mr-3 text-blue-400" />
                            Contactos
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSaveToCategory(user, "stores")}
                            className="w-full flex items-center px-3 py-2 text-sm text-white hover:bg-[#2b5278] rounded-md"
                          >
                            <Store className="w-4 h-4 mr-3 text-orange-400" />
                            Mis Tiendas
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSaveToCategory(user, "restaurants")}
                            className="w-full flex items-center px-3 py-2 text-sm text-white hover:bg-[#2b5278] rounded-md"
                          >
                            <UtensilsCrossed className="w-4 h-4 mr-3 text-green-400" />
                            Mis Restaurantes
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewChatDialog;
